<script setup>
import { ref, computed, watch } from 'vue'
import { qsQueue } from '../quickset.js'

const props = defineProps({
  poolsByManufacturer: { type: Object, required: true },
  manufacturer:        { type: String, required: true }
})

const lines               = ref([{ pool_key: '', marbletech: false }])
const currentManufacturer = ref(props.manufacturer)

const availablePools = computed(() =>
  props.poolsByManufacturer[currentManufacturer.value] || []
)

watch(() => props.manufacturer, val => {
  currentManufacturer.value = val
  lines.value = lines.value.map(l => ({ ...l, pool_key: '' }))
})

function addLine()         { lines.value.push({ pool_key: '', marbletech: false }) }
function removeLine(index) { if (lines.value.length > 1) lines.value.splice(index, 1) }

// ── Respond to QuickSet commands from application.js ─────────────────────────

function applyCommand(cmd) {
  if (cmd.type === 'setManufacturer') {
    currentManufacturer.value = cmd.arg
    lines.value = lines.value.map(l => ({ ...l, pool_key: '' }))
  } else if (cmd.type === 'setPool') {
    const i = lines.value.findIndex(l => !l.pool_key)
    if (i >= 0) lines.value[i].pool_key = cmd.arg
    else        lines.value.push({ pool_key: cmd.arg, marbletech: false })
  } else if (cmd.type === 'removeLine') {
    const i = lines.value.findIndex(l => l.pool_key === cmd.arg)
    if (i < 0) return
    if (lines.value.length > 1) lines.value.splice(i, 1)
    else                        lines.value[0].pool_key = ''
  } else if (cmd.type === 'reset') {
    currentManufacturer.value = props.manufacturer
    lines.value = [{ pool_key: '', marbletech: false }]
  } else if (cmd.type === 'setMarbletech') {
    let lastIdx = -1
    for (let i = lines.value.length - 1; i >= 0; i--) {
      if (lines.value[i].pool_key) { lastIdx = i; break }
    }
    if (lastIdx >= 0) lines.value[lastIdx].marbletech = cmd.arg
  } else if (cmd.type === 'setMarbletechAll') {
    // Call Notes drives marbletech as an all-lines flag; set every populated line.
    lines.value.forEach(l => { if (l.pool_key) l.marbletech = cmd.arg })
  }
}

watch(qsQueue, (cmds) => {
  if (!cmds.length) return
  cmds.forEach(applyCommand)
  qsQueue.value = []
})
</script>

<template>
  <div class="space-y-3">
    <div v-for="(line, index) in lines" :key="index"
         class="flex gap-3 items-center">
      <select
        v-model="line.pool_key"
        :name="`pool_lines[${index}][pool_key]`"
        class="select select-bordered flex-1 select-sm"
      >
        <option value="" disabled>Select pool model&hellip;</option>
        <option v-for="[name, key] in availablePools" :key="key" :value="key">
          {{ name }}
        </option>
      </select>

      <label class="flex items-center gap-1.5 text-sm cursor-pointer whitespace-nowrap">
        <input
          type="checkbox"
          v-model="line.marbletech"
          :name="`pool_lines[${index}][marbletech]`"
          class="checkbox checkbox-primary checkbox-xs"
        />
        <span class="label-text text-xs">Marbletech</span>
      </label>

      <button
        v-if="lines.length > 1"
        type="button"
        @click="removeLine(index)"
        class="btn btn-ghost btn-xs text-error"
      >&times;</button>
    </div>

    <button
      type="button"
      @click="addLine"
      class="btn btn-outline btn-accent btn-sm"
    >
      + Add Option
    </button>
  </div>
</template>
